/**
 * Project file schema: validation, migration, serialisation.
 *
 * Sharing `.json` projects is an advertised feature, which makes this a trust
 * boundary (PRD §6.5). Every field arriving from a file is treated as hostile
 * until checked: unknown keys are rejected rather than ignored, numbers are
 * checked for finiteness, and every expression is parsed — so a project
 * naming a function outside the allowlist fails here, at import, rather than
 * somewhere deep in a Worker.
 *
 * Validation is deliberately strict and verbose. A file that fails should say
 * exactly which field was wrong; a file that passes should be incapable of
 * surprising the engine.
 */

import type {
  Project,
  Variable,
  CorrelationGroup,
  CorrelationPair,
  Intermediate,
  OutputSpec,
} from '../engine/model.ts';
import type { Distribution } from '../engine/distributions.ts';
import { validate as validateDistribution } from '../engine/distributions.ts';
import { parse } from '../engine/expr/parse.ts';
import { buildModel } from '../engine/model.ts';

export const CURRENT_SCHEMA_VERSION = 1;

/** Refuse absurd files before doing any work on them. */
export const MAX_PROJECT_BYTES = 8 * 1024 * 1024;
const MAX_VARIABLES = 2000;
const MAX_OUTPUTS = 200;
const MAX_EXPR_LENGTH = 10_000;
const MAX_ID_LENGTH = 128;
const MAX_DISCRETE_VALUES = 10_000;
const MAX_SAMPLES = 1_000_000;
const MAX_CORRELATIONS = 10_000;

export class SchemaError extends Error {
  constructor(
    message: string,
    readonly path: string,
  ) {
    super(path ? `${path}: ${message}` : message);
    this.name = 'SchemaError';
  }
}

const fail = (path: string, message: string): never => {
  throw new SchemaError(message, path);
};

function obj(v: unknown, path: string): Record<string, unknown> {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) {
    fail(path, 'expected an object');
  }
  return v as Record<string, unknown>;
}

function arr(v: unknown, path: string, max: number): unknown[] {
  if (!Array.isArray(v)) fail(path, 'expected an array');
  const a = v as unknown[];
  if (a.length > max) fail(path, `too many entries (max ${max})`);
  return a;
}

function num(v: unknown, path: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    fail(path, 'expected a finite number');
  }
  return v as number;
}

function optNum(v: unknown, path: string): number | undefined {
  return v === undefined || v === null ? undefined : num(v, path);
}

function str(v: unknown, path: string, maxLength: number): string {
  if (typeof v !== 'string') fail(path, 'expected a string');
  const s = v as string;
  if (s.length > maxLength) fail(path, `too long (max ${maxLength} characters)`);
  return s;
}

function optStr(v: unknown, path: string, maxLength: number): string | undefined {
  return v === undefined || v === null ? undefined : str(v, path, maxLength);
}

/**
 * Identifiers become names in expressions, so they must be lexable as such.
 * Rejecting anything else here means the parser can never meet a name it
 * cannot represent.
 */
function identifier(v: unknown, path: string): string {
  const s = str(v, path, MAX_ID_LENGTH);
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(s)) {
    fail(path, `'${s}' is not a valid identifier (letters, digits, underscore; not starting with a digit)`);
  }
  return s;
}

/** Reject unknown keys rather than silently dropping them. */
function noExtraKeys(o: Record<string, unknown>, allowed: string[], path: string): void {
  for (const k of Object.keys(o)) {
    if (!allowed.includes(k)) fail(path, `unknown field '${k}'`);
  }
}

function parseDistribution(v: unknown, path: string): Distribution {
  const o = obj(v, path);
  const kind = str(o.kind, `${path}.kind`, 32);

  let d: Distribution;
  switch (kind) {
    case 'fixed':
      noExtraKeys(o, ['kind', 'value'], path);
      d = { kind: 'fixed', value: num(o.value, `${path}.value`) };
      break;
    case 'uniform':
      noExtraKeys(o, ['kind', 'min', 'max'], path);
      d = { kind: 'uniform', min: num(o.min, `${path}.min`), max: num(o.max, `${path}.max`) };
      break;
    case 'normal':
      noExtraKeys(o, ['kind', 'mu', 'sigma'], path);
      d = { kind: 'normal', mu: num(o.mu, `${path}.mu`), sigma: num(o.sigma, `${path}.sigma`) };
      break;
    case 'truncnormal':
      noExtraKeys(o, ['kind', 'mu', 'sigma', 'min', 'max'], path);
      d = {
        kind: 'truncnormal',
        mu: num(o.mu, `${path}.mu`),
        sigma: num(o.sigma, `${path}.sigma`),
        min: num(o.min, `${path}.min`),
        max: num(o.max, `${path}.max`),
      };
      break;
    case 'triangular':
      noExtraKeys(o, ['kind', 'min', 'mode', 'max'], path);
      d = {
        kind: 'triangular',
        min: num(o.min, `${path}.min`),
        mode: num(o.mode, `${path}.mode`),
        max: num(o.max, `${path}.max`),
      };
      break;
    case 'lognormal':
      noExtraKeys(o, ['kind', 'muLog', 'sigmaLog'], path);
      d = {
        kind: 'lognormal',
        muLog: num(o.muLog, `${path}.muLog`),
        sigmaLog: num(o.sigmaLog, `${path}.sigmaLog`),
      };
      break;
    case 'discrete': {
      noExtraKeys(o, ['kind', 'values', 'weights'], path);
      const values = arr(o.values, `${path}.values`, MAX_DISCRETE_VALUES).map((x, i) =>
        num(x, `${path}.values[${i}]`),
      );
      const weights = arr(o.weights, `${path}.weights`, MAX_DISCRETE_VALUES).map((x, i) =>
        num(x, `${path}.weights[${i}]`),
      );
      d = { kind: 'discrete', values, weights };
      break;
    }
    case 'empirical': {
      noExtraKeys(o, ['kind', 'samples'], path);
      const raw = arr(o.samples, `${path}.samples`, MAX_SAMPLES);
      // Sorting on import rather than trusting the file: the sorted order is a
      // load-bearing invariant of the sampler, and a hand-edited or
      // third-party file has no reason to have honoured it.
      const samples = raw
        .map((x, i) => num(x, `${path}.samples[${i}]`))
        .sort((a, b) => a - b);
      d = { kind: 'empirical', samples };
      break;
    }
    default:
      return fail(`${path}.kind`, `unknown distribution '${kind}'`);
  }

  // Reuse the engine's own parameter checks so a file can never construct a
  // distribution the engine would consider invalid.
  try {
    validateDistribution(d, path);
  } catch (e) {
    fail(path, (e as Error).message);
  }
  return d;
}

function parseExpression(v: unknown, path: string): string {
  const s = str(v, path, MAX_EXPR_LENGTH);
  try {
    parse(s);
  } catch (e) {
    fail(path, (e as Error).message);
  }
  return s;
}

function parseVariable(v: unknown, path: string): Variable {
  const o = obj(v, path);
  // `scope` is accepted and discarded. It labelled a variable as shared or
  // per-component up to v1.2 and never reached the engine, so dropping it
  // changes no result — but rejecting it here would make every project file
  // written before v1.3 unopenable, which is a real cost for a dead field.
  noExtraKeys(o, ['id', 'name', 'unit', 'scope', 'distribution', 'group'], path);

  return {
    id: identifier(o.id, `${path}.id`),
    name: optStr(o.name, `${path}.name`, 200),
    unit: optStr(o.unit, `${path}.unit`, 32),
    distribution: parseDistribution(o.distribution, `${path}.distribution`),
    group: o.group === undefined || o.group === null
      ? undefined
      : identifier(o.group, `${path}.group`),
  };
}

function parseGroup(v: unknown, path: string): CorrelationGroup {
  const o = obj(v, path);
  noExtraKeys(o, ['id', 'rho', 'label'], path);
  const rho = num(o.rho, `${path}.rho`);
  if (rho < 0 || rho > 1) fail(`${path}.rho`, `must lie in [0, 1], got ${rho}`);
  return {
    id: identifier(o.id, `${path}.id`),
    rho,
    label: optStr(o.label, `${path}.label`, 200),
  };
}

function parseCorrelation(v: unknown, path: string): CorrelationPair {
  const o = obj(v, path);
  noExtraKeys(o, ['a', 'b', 'rho'], path);
  const rho = num(o.rho, `${path}.rho`);
  // Unlike a group's rho, an explicit correlation may be negative — that is the
  // whole point of offering a matrix.
  if (rho < -1 || rho > 1) fail(`${path}.rho`, `must lie in [-1, 1], got ${rho}`);
  return {
    a: identifier(o.a, `${path}.a`),
    b: identifier(o.b, `${path}.b`),
    rho,
  };
}

function parseIntermediate(v: unknown, path: string): Intermediate {
  const o = obj(v, path);
  noExtraKeys(o, ['id', 'expr', 'unit'], path);
  return {
    id: identifier(o.id, `${path}.id`),
    expr: parseExpression(o.expr, `${path}.expr`),
    unit: optStr(o.unit, `${path}.unit`, 32),
  };
}

function parseOutput(v: unknown, path: string): OutputSpec {
  const o = obj(v, path);
  noExtraKeys(o, ['id', 'expr', 'unit', 'lsl', 'usl', 'target'], path);
  const lsl = optNum(o.lsl, `${path}.lsl`);
  const usl = optNum(o.usl, `${path}.usl`);
  if (lsl !== undefined && usl !== undefined && lsl >= usl) {
    fail(path, `LSL (${lsl}) must be below USL (${usl})`);
  }
  return {
    id: identifier(o.id, `${path}.id`),
    expr: parseExpression(o.expr, `${path}.expr`),
    unit: optStr(o.unit, `${path}.unit`, 32),
    lsl,
    usl,
    target: optNum(o.target, `${path}.target`),
  };
}

/**
 * Validate an already-parsed JSON value into a Project.
 *
 * Also runs `buildModel`, so cross-field problems — undefined references,
 * circular intermediates, a group that no variable joins — are caught at
 * import rather than at run time.
 */
export function validateProject(value: unknown): Project {
  const o = obj(value, '');

  noExtraKeys(
    o,
    [
      'schemaVersion',
      'name',
      'seed',
      'variables',
      'groups',
      'correlations',
      'repairCorrelation',
      'intermediates',
      'outputs',
    ],
    '',
  );

  const version = num(o.schemaVersion, 'schemaVersion');
  if (!Number.isInteger(version) || version < 1) {
    fail('schemaVersion', `expected a positive integer, got ${version}`);
  }
  if (version > CURRENT_SCHEMA_VERSION) {
    fail(
      'schemaVersion',
      `file was written by a newer version of tola (schema ${version}, this build understands ${CURRENT_SCHEMA_VERSION})`,
    );
  }

  const seed = num(o.seed, 'seed');
  if (!Number.isInteger(seed) || seed < 0 || seed > 0xffffffff) {
    fail('seed', 'must be an integer in [0, 4294967295]');
  }

  const project: Project = {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    name: str(o.name ?? 'Untitled', 'name', 200),
    seed,
    variables: arr(o.variables, 'variables', MAX_VARIABLES).map((v, i) =>
      parseVariable(v, `variables[${i}]`),
    ),
    groups: o.groups === undefined
      ? undefined
      : arr(o.groups, 'groups', MAX_VARIABLES).map((v, i) => parseGroup(v, `groups[${i}]`)),
    correlations: o.correlations === undefined
      ? undefined
      : arr(o.correlations, 'correlations', MAX_CORRELATIONS).map((v, i) =>
          parseCorrelation(v, `correlations[${i}]`),
        ),
    repairCorrelation:
      o.repairCorrelation === undefined ? undefined : Boolean(o.repairCorrelation),
    intermediates: o.intermediates === undefined
      ? undefined
      : arr(o.intermediates, 'intermediates', MAX_VARIABLES).map((v, i) =>
          parseIntermediate(v, `intermediates[${i}]`),
        ),
    outputs: arr(o.outputs, 'outputs', MAX_OUTPUTS).map((v, i) =>
      parseOutput(v, `outputs[${i}]`),
    ),
  };

  try {
    buildModel(project);
  } catch (e) {
    fail('', (e as Error).message);
  }

  return project;
}

/** Parse and validate a project file's text. */
export function parseProjectFile(text: string): Project {
  if (text.length > MAX_PROJECT_BYTES) {
    throw new SchemaError(
      `file is too large (${(text.length / 1e6).toFixed(1)} MB, limit ${MAX_PROJECT_BYTES / 1e6} MB)`,
      '',
    );
  }

  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (e) {
    throw new SchemaError(`not valid JSON — ${(e as Error).message}`, '');
  }

  return validateProject(value);
}

export function serializeProject(project: Project): string {
  return JSON.stringify(
    {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      name: project.name,
      seed: project.seed,
      variables: project.variables,
      groups: project.groups,
      correlations: project.correlations,
      repairCorrelation: project.repairCorrelation,
      intermediates: project.intermediates,
      outputs: project.outputs,
    },
    null,
    2,
  );
}
